// Sugalarm — cycle 404: "I can help after all".
//
// A hero who takes an escalation call can now press 3 — "I can't help right
// now" — and stop being RUNG for the rest of THAT episode. The server keeps
// the refusal on the alert row (`alerts.episode_opt_outs`), so it dies with
// the episode; there is no standing do-not-call list here and none may be
// inferred from one. Texts are never suppressed either way.
//
// This file owns the one question the SPA asks about that state: does the
// person holding THIS screen have a way back into the call search?
//
// ⚠ THE DECISION IS PURE ON PURPOSE. It is the same shape the cycle-398
// stand-down predicate uses and for the same reason: the surface that
// renders it sits inside a live urgent-low takeover, which is the single
// hardest thing in this app to stand up in a harness. A rule that can only
// be exercised by mounting the alarm screen is a rule that gets refactored
// out unpinned.
//
// ⚠ EVERY TERM IS A "SHOW" TERM, AND THE DEFAULT IS HIDDEN. Nothing here can
// remove a control from an alarm surface — this action only ever ADDS a
// secondary affordance beside Snooze — so the failure that matters is
// showing it to the wrong person, not withholding it. Unknown, missing and
// malformed inputs therefore all resolve to `false`.

import type { AlertLevel } from "./types";
import type { FrameVariant } from "../hooks/useFrameVariant";

/** Verbatim operator copy. Do not reword, do not sentence-case, do not
 *  shorten to fit a layout — the phrase is the whole affordance, and it has
 *  to read as the plain-language undo of "I can't help right now". */
export const OPT_OUT_UNDO_LABEL = "I can help after all";

/** Everything the decision is allowed to look at. Deliberately a flat value
 *  object: no clocks, no fetches, no refs. */
export interface OptOutUndoInput {
  /** The PERSISTED episode level (`episode.episodeLevel`), not the live
   *  reading's instantaneous zone — the same signal every other urgent
   *  control surface gates on since cycle 81, so the action cannot blink
   *  out mid-recovery-dwell while the alarm is still sounding. */
  level: AlertLevel | null;
  /** `episode.isLive` — the cycle-81 "this episode is live" predicate. */
  isLive: boolean;
  variant: FrameVariant;
  /** The signed-in account's own linked contact id, or null when we do not
   *  know it (not signed in, no linked contact, or the lookup failed). */
  myContactId: string | null;
  /** `alert.episode_opt_outs`. Missing / null / empty are all "nobody has
   *  opted out", which is the overwhelmingly common case. */
  episodeOptOuts: readonly string[] | null | undefined;
}

export function optOutUndoVisible(input: OptOutUndoInput): boolean {
  const { level, isLive, variant, myContactId, episodeOptOuts } = input;
  // ⚠ THE KIOSK CAN NEVER SHOW THIS, AND THE ACCOUNT MATCH IS NOT WHAT
  // PROTECTS IT. A kiosk device session authenticates AS the household
  // owner, so if the owner is also a linked hero who pressed 3, their
  // contact id WILL match on the tablet — and the tablet is a shared panel
  // on a counter, not a person. Only this guard stands between a bedside
  // display and a personal "I can help after all". Same shape, same
  // reasoning, as the cycle-403 `callQuietActive` kiosk guard.
  if (variant === "kiosk") return false;
  // Calls only ever escalate on the low side, and press-3 only exists on
  // that rail — so there is nothing to reinstate anywhere else.
  if (level !== "urgent_low") return false;
  if (!isLive) return false;
  // No identity → no action. The lookup fails open toward HIDDEN (see the
  // file header), so a failed contacts fetch simply means no affordance.
  if (!myContactId) return false;
  if (!episodeOptOuts || episodeOptOuts.length === 0) return false;
  return episodeOptOuts.includes(myContactId);
}
